import React from 'react';
import { toast } from "@/components/ui/sonner";
import { useOptionalFieldValidation } from '@/components/validation';
import { useUserConfigState, useUserConfigDispatch } from '@/context/UserConfigContext';
import { TravelConfigForm } from '@/components/config_forms/TravelConfigForm';
import type { UserConfigurationState } from '@/types/userConfiguration';
import { getDefaultExpenseCategories } from '@/constants/expenses';
import type { ExpenseCategory } from '@/types/ExpenseCategory';
import { cn } from '@/lib/utils';

interface TravelConfigProps {
  noCard?: boolean;
}

export const TravelConfig: React.FC<TravelConfigProps> = ({ noCard = false }) => {
  const state = useUserConfigState();
  const { updateTravelConfig, updateBillingConfig } = useUserConfigDispatch();
  const {
    needsTravel,
    applyPerDiem,
    perDiemBaseRate,
    travelDayRateMultiplier,
    multiplierAppliesToOverages,
    travelDistanceThreshold,
    expenseConfig
  } = state;

  const perDiem = useOptionalFieldValidation('perDiemBaseRate');
  const multiplier = useOptionalFieldValidation('travelDayRateMultiplier');
  const distance = useOptionalFieldValidation('travelDistanceThreshold');

  const perDiemInputProps: Record<string, unknown> = {
    'data-validation-key': 'perDiemBaseRate',
    value: perDiemBaseRate ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value ? parseFloat(e.target.value) : null;
      updateTravelConfig({ perDiemBaseRate: v });
    },
    onBlur: perDiem.onBlur,
    className: cn(perDiem.hasError && "border-destructive focus-visible:ring-destructive focus-visible:border-destructive"),
  };

  const travelMultiplierInputProps: Record<string, unknown> = {
    'data-validation-key': 'travelDayRateMultiplier',
    value: travelDayRateMultiplier ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value ? parseFloat(e.target.value) : null;
      updateTravelConfig({ travelDayRateMultiplier: v });
    },
    onBlur: multiplier.onBlur,
    className: cn(multiplier.hasError && "border-destructive focus-visible:ring-destructive focus-visible:border-destructive"),
  };

  const distanceThresholdInputProps: Record<string, unknown> = {
    'data-validation-key': 'travelDistanceThreshold',
    value: travelDistanceThreshold ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value ? parseFloat(e.target.value) : null;
      updateTravelConfig({ travelDistanceThreshold: v });
    },
    onBlur: distance.onBlur,
    className: cn(distance.hasError && "border-destructive focus-visible:ring-destructive focus-visible:border-destructive"),
  };

  // Travel expense categories: filter travel-scoped from all categories
  const allCategories = expenseConfig?.expenseCategories ?? getDefaultExpenseCategories();
  const travelCategories = allCategories.filter((c) => c.scope === 'travel_only');

  const handleTravelCategoriesChange = (updatedTravelCategories: ExpenseCategory[]) => {
    // Merge: replace travel-scoped categories, keep all-scoped categories
    const allScopeCategories = allCategories.filter((c) => c.scope === 'all');
    const merged = [...allScopeCategories, ...updatedTravelCategories];
    updateBillingConfig({
      expenseConfig: {
        ...(expenseConfig || {}),
        expenseCategories: merged,
      },
    });
  };

  return (
    <TravelConfigForm
      title="Travel Policies"
      description="Configure how you handle travel days and per diem rates for out-of-town work"
      needsTravel={needsTravel}
      applyPerDiem={applyPerDiem}
      usesGsaRate={state.usesGsaRate}
      multiplierAppliesToOverages={multiplierAppliesToOverages || false}
      onNeedsTravelChange={(checked) => {
        const updates: Partial<UserConfigurationState> = { needsTravel: checked };
        if (checked) {
          updates.applyPerDiem = true;
        }
        updateTravelConfig(updates);
        toast.success(checked ? "Travel policies enabled" : "Travel policies disabled");
      }}
      onApplyPerDiemChange={(checked) => {
        updateTravelConfig({ applyPerDiem: checked });
        toast.success(checked ? "Per Diem enabled" : "Per Diem disabled");
      }}
      onUsesGsaRateChange={(checked) => {
        updateTravelConfig({ usesGsaRate: checked });
        toast.success(
          checked ? "Using GSA per diem rates" : "Using default per diem rate"
        );
      }}
      onMultiplierAppliesToOveragesChange={(checked) => {
        updateTravelConfig({ multiplierAppliesToOverages: checked });
        toast.success(
          checked
            ? "Travel multiplier will be applied to overtime hours"
            : "Travel multiplier will not be applied to overtime hours"
        );
      }}
      perDiemInputProps={{
        ...perDiemInputProps,
        'data-testid': "per-diem-input",
      }}
      travelMultiplierInputProps={{
        ...travelMultiplierInputProps,
        'data-testid': "travel-multiplier-input",
      }}
      perDiemError={perDiem.errorElement}
      travelMultiplierError={multiplier.errorElement}
      travelDistanceThreshold={travelDistanceThreshold}
      onTravelDistanceThresholdChange={(value) => updateTravelConfig({ travelDistanceThreshold: value })}
      travelDistanceThresholdInputProps={distanceThresholdInputProps}
      travelDistanceThresholdError={distance.errorElement}
      travelExpenseCategories={travelCategories}
      onTravelExpenseCategoriesChange={handleTravelCategoriesChange}
      noCard={noCard}
    />
  );
};
